// Occupation du domaine public (H5-02, M09, lot L2-B) : l'autorisation naît de la décision favorable
// d'un dossier d'une démarche du domaine « domaine_public » (« occupation du domaine public » au
// lancement) ; elle porte catégorie, emprise géographique (polygone WGS84) ou position avec surface,
// période, et la créance émise par le module des recettes. Routes GeoJSON de la carte territoriale
// (marchés en points, autorisations en emprises) filtrées par emprise rectangulaire (bbox).
import { z } from 'zod';
import { schemaBbox } from './dossiers';
import { schemaPosition } from './marches-structure';

export const CATEGORIES_OCCUPATION = {
  terrasse: 'Terrasse',
  kiosque: 'Kiosque',
  installation: 'Installation temporaire',
  manifestation: 'Manifestation',
} as const;
export type CategorieOccupation = keyof typeof CATEGORIES_OCCUPATION;
export const CODES_CATEGORIES_OCCUPATION = Object.keys(CATEGORIES_OCCUPATION) as [CategorieOccupation, ...CategorieOccupation[]];

export const ETATS_AUTORISATION = ['active', 'expiree', 'retiree', 'annulee'] as const;
export type EtatAutorisation = (typeof ETATS_AUTORISATION)[number];

/** Rubrique du catalogue des recettes proposée par défaut (démarche « occupation du domaine public »). */
export const RUBRIQUE_OCCUPATION_DEFAUT = 'odp_m2_mois';
export const NATURE_OBJET_AUTORISATION = 'autorisation_domaine_public' as const;
/** État de la créance d'une autorisation : émise, ou en attente du module des recettes (H-L2B-02). */
export const ETATS_CREANCE_AUTORISATION = ['emise', 'en_attente'] as const;

const lonLat = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);
const anneau = z
  .array(lonLat)
  .min(4, 'Un anneau compte au moins quatre positions')
  .max(500)
  .refine((a) => a[0]![0] === a[a.length - 1]![0] && a[0]![1] === a[a.length - 1]![1], 'Anneau non fermé : la dernière position répète la première');

/** Polygone GeoJSON (RFC 7946) : un anneau extérieur et, au besoin, des trous. */
export const schemaPolygoneGeoJson = z.strictObject({
  type: z.literal('Polygon'),
  coordinates: z.array(anneau).min(1).max(10),
});
export type PolygoneGeoJson = z.infer<typeof schemaPolygoneGeoJson>;

export const schemaModificationEmprise = z
  .strictObject({ emprise: schemaPolygoneGeoJson.nullable(), position: schemaPosition.nullable() })
  .partial()
  .refine((v) => v.emprise !== undefined || v.position !== undefined, 'Indiquer l\'emprise ou la position');

export const schemaRetraitAutorisation = z.strictObject({
  motif: z.string().trim().min(5).max(1000),
});

export const schemaFiltreAutorisations = z.strictObject({
  etat: z.enum(ETATS_AUTORISATION).optional(),
  categorie: z.enum(CODES_CATEGORIES_OCCUPATION).optional(),
  /** Autorisations actives dont la période contient aujourd'hui. */
  en_cours: z.enum(['oui', 'non']).optional(),
  bbox: schemaBbox.optional(),
  limite: z.coerce.number().int().min(1).max(500).default(100),
});

export const schemaFiltreCarte = z.strictObject({
  bbox: schemaBbox.optional(),
  /** Autorisations : toutes les autorisations actives (défaut) ou seulement celles en cours aujourd'hui. */
  en_cours: z.enum(['oui', 'non']).optional(),
});

/** Propriétés publiées sur la carte : aucune donnée personnelle (ni titulaire, ni commerçant). */
export interface ProprietesCarteMarche {
  objet: 'marche';
  id: string;
  code: string;
  nom: string;
  emplacements: number;
  occupes: number;
}
export interface ProprietesCarteAutorisation {
  objet: 'autorisation_domaine_public';
  id: string;
  numero: string;
  categorie: CategorieOccupation;
  categorie_libelle: string;
  surface_m2: number;
  debut: string;
  fin: string;
  etat: EtatAutorisation;
  /** emprise : polygone saisi ; position : point du dossier ou saisi (la surface est dans les propriétés). */
  geometrie: 'emprise' | 'position';
  /** Point représentatif (sur la surface de l'emprise, ou la position) : marqueur du composant CarteTerritoire. */
  centre: { longitude: number; latitude: number };
}
